import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripDotsVertical } from '@fortawesome/pro-light-svg-icons/faGripDotsVertical';
import { faEye } from '@fortawesome/pro-light-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons/faEyeSlash';
import { SerializedWidget } from 'fym-common/src/db/serializersTypes';
import { TypedMemo } from '../../../common/types';
import { blockMessages } from './blockMessages';
import { BlockDescription } from './describeBlock';
import { BlockMenuAction, SMLBlockMenu } from './SMLBlockMenu';
import { SMLBlockThumb } from './SMLBlockThumb';
import { BlockDrag } from './useBlockDrag';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLBlockRow.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SMLBlockRowHeadProps {
  widget: SerializedWidget;
  description: BlockDescription;
  isNew: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  canDuplicate: boolean;
  drag: BlockDrag;
  onToggle: () => void;
  onAction: (action: BlockMenuAction) => void;
}

/** The line of a row (design d0, 1–6): handle, picture, name, type, summary, eye and menu. */
const SMLBlockRowHeadComponent: React.FC<SMLBlockRowHeadProps> = ({
  widget,
  description,
  isNew,
  canMoveUp,
  canMoveDown,
  canDuplicate,
  drag,
  onToggle,
  onAction,
}) => {
  const intl = useIntl();
  const isHidden = widget.hidden === true;
  const { onDragStart, onDragEnd, onHandleKeyDown } = drag;
  const handleDragStart = useCallback(
    (event: React.DragEvent) => onDragStart(widget.uuid, event),
    [onDragStart, widget]
  );
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => onHandleKeyDown(widget.uuid, event),
    [onHandleKeyDown, widget.uuid]
  );
  const onEye = useCallback(() => onAction('toggleHidden'), [onAction]);
  const isPicked = drag.drag?.widgetUUID === widget.uuid && drag.drag.isKeyboard;

  return (
    <div className={clsx(styles.head, isHidden && styles.headHidden)}>
      <button
        type="button"
        className={clsx(styles.grip, isPicked && styles.gripPicked)}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={onDragEnd}
        onKeyDown={handleKeyDown}
        aria-label={intl.formatMessage(blockMessages.dragHandle, { name: description.name })}
        aria-pressed={isPicked}
        title={intl.formatMessage(blockMessages.dragHelp)}
        data-testid="block-drag-handle"
      >
        <FontAwesomeIcon icon={faGripDotsVertical} />
      </button>
      <button type="button" className={styles.main} onClick={onToggle} data-testid="block-row-toggle">
        <SMLBlockThumb widget={widget} isWarn={description.tone === 'warn'} />
        <span className={styles.text}>
          <span className={styles.name}>
            <span className={styles.nameText} data-testid="block-row-name">
              {description.name}
            </span>
            {description.typeLabel !== null && <span className={styles.type}>{description.typeLabel}</span>}
            {isNew && <span className={styles.newTag}>{intl.formatMessage(blockMessages.newTag)}</span>}
          </span>
          <span
            className={clsx(
              styles.summary,
              description.tone === 'warn' && styles.summaryWarn,
              description.tone === 'pos' && styles.summaryPos
            )}
            data-testid="block-row-summary"
          >
            {description.summary}
          </span>
        </span>
      </button>
      <span className={styles.actions}>
        <button
          type="button"
          className={clsx(styles.iconButton, isHidden && styles.iconButtonOn)}
          onClick={onEye}
          aria-label={intl.formatMessage(isHidden ? blockMessages.show : blockMessages.hide)}
          aria-pressed={isHidden}
          data-testid="block-row-eye"
        >
          <FontAwesomeIcon icon={isHidden ? faEyeSlash : faEye} />
        </button>
        <SMLBlockMenu
          name={description.name}
          isHidden={isHidden}
          canMoveUp={canMoveUp}
          canMoveDown={canMoveDown}
          canDuplicate={canDuplicate}
          onAction={onAction}
        />
      </span>
    </div>
  );
};

export const SMLBlockRowHead = TypedMemo(SMLBlockRowHeadComponent);
